/**
 * @fileoverview Org portal — program detail. One program's instructors, what
 * has actually been taught in it, who hears about it and on what schedule.
 *
 * Counting rule: a *session* is one taught meeting. A class session counts once
 * however many students attended — a class lesson is a single shared record
 * (see routes/org.py) — so `students_reached` carries the reach figure instead
 * of inflating the session count.
 *
 * The mail switches and the recipient list live on the PROGRAM record, saved
 * through the same all-optional `PUT /org/programs/:id` diff the listing edit
 * uses, so an unset field is never silently reset. Each write is optimistic
 * and rolls back on failure — a toggle that lies about having saved is worse
 * than one that is slow.
 */

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import ExportMenu, { CSV_TYPE, XLSX_TYPE, exportSlug } from "@/components/studio/ExportMenu";
import { useTabParam } from "@/lib/tabParam";
import { Card, PageShell, SectionHeader, ListRow, Avatar, Icon, Tag, Button, IconButton, StatCard, SegmentedControl } from "@/components/studio/primitives";
import LessonFilterBar from "@/components/studio/LessonFilterBar";
import { filterLessons, PERIODS, sortChronologically } from "@/lib/lessonHistory";
import {
  SESSION_KINDS,
  SESSION_STATUSES,
  STATUS_FILTERS,
  UPCOMING_PERIOD,
  WINDOW_PERIOD,
  byStatus,
  distinctOptions,
  inWindow,
  sessionRow,
  statusFromFilter,
} from "@/lib/orgSessions";
import SessionList from "@/components/org/SessionList";
import { MoreRowsBar, usePagedRows } from "@/components/studio/MoreRows";
import useOrgNotesClear from "@/components/org/useOrgNotesClear";
import useOrgSessionActions, { ArchivedNote } from "@/components/org/useOrgSessionActions";
import { clearanceLine, clearanceTag } from "@/lib/notesClearance";
import ProgramLocationSettings from "@/components/org/ProgramLocationSettings";
import ProgramListingSettings from "@/components/org/ProgramListingSettings";
import ProgramEmailSettings from "@/components/org/ProgramEmailSettings";
import ProgramSubstituteSettings from "@/components/org/ProgramSubstituteSettings";
import ProgramLifecycleSettings from "@/components/org/ProgramLifecycleSettings";
import SubstituteRequestsCard from "@/components/org/SubstituteRequestsCard";
import InviteInstructorsDialog from "@/components/org/InviteInstructorsDialog";
import AddViewerDialog, { permissionLabels } from "@/components/org/AddViewerDialog";
import AddSubstituteDialog from "@/components/org/AddSubstituteDialog";
import AddClassDialog from "@/components/org/AddClassDialog";
import AssignClassInstructorDialog from "@/components/org/AssignClassInstructorDialog";
import AnnouncementDialog from "@/components/org/AnnouncementDialog";
import ProgramContactsCard from "@/components/org/ProgramContactsCard";
import ResourcesSection from "@/components/org/ResourcesSection";
import { OrgAuthContext } from "@/App";

function when(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* Four questions a program is asked (O2): how it is going, what happened
   session by session, who it reaches, and how it is set up. The tiles above
   the bar open the tab that holds the rows behind their number. */
const PROGRAM_TABS = [
  { id: "overview", label: "Overview", testId: "program-tab-overview" },
  { id: "sessions", label: "Sessions", testId: "program-tab-sessions" },
  { id: "students", label: "Students", testId: "program-tab-students" },
  { id: "settings", label: "Settings", testId: "program-tab-settings" },
];

const CONSENT_LABELS = { granted: "Consent", requested: "Asked", declined: "Declined", revoked: "Withdrawn", none: "No consent" };

/* The period the Sessions tab opens on when a link or a reload names only a
   filter (the tiles choose their own): the upcoming window for upcoming
   sessions, all time for a notes notice, else the taught window. All time
   because the Dashboard counts requests to clear a missing-notes notice over
   60 days and links here, so the list must hold every one it counted. Mirrors
   the iOS program Sessions tab. */
const NOTICE_STATUSES = new Set(["missing", "cleared"]);
function openingPeriod(filter) {
  const status = statusFromFilter(filter);
  if (status === "upcoming") return UPCOMING_PERIOD;
  return NOTICE_STATUSES.has(status) ? "all" : WINDOW_PERIOD;
}

export default function OrgProgramDetailPage() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { org } = useContext(OrgAuthContext) || {};
  const [detail, setDetail] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  /* The tab lives in the URL (F6), so a link into Settings — the class
     page's publish gate points here — lands on it. */
  const { tab, filter, setTab } = useTabParam(PROGRAM_TABS, "overview");
  /* The rows behind the tiles, read when their tab opens (O2). */
  const [sessions, setSessions] = useState(null);
  const [students, setStudents] = useState(null);
  const [sessionBar, setSessionBar] = useState(() => ({ period: openingPeriod(filter), instructor: null, classId: null, search: "" }));
  const [dialog, setDialog] = useState(null); // "instructors" | "viewer" | "substitute" | "class" | "assign" | "announce"
  // The class whose instructor is being named. Held apart from `dialog` so
  // the dialog can render the class it is about rather than re-finding it.
  const [assigning, setAssigning] = useState(null);
  // Archived classes (O5) are kept, not shown: one line under the list says
  // how many there are and opens them.
  const [showArchived, setShowArchived] = useState(false);
  const [viewers, setViewers] = useState([]);
  // The viewer whose access is being changed (their row opens the dialog).
  const [editingViewer, setEditingViewer] = useState(null);
  const [substitutes, setSubstitutes] = useState([]);
  const [week, setWeek] = useState(null);
  const [notesOwed, setNotesOwed] = useState(null);
  // The Overview's Missing notes list opens on request: a term that has just
  // started can owe dozens of sessions, and the rest of the page sits under it.
  const [owedOpen, setOwedOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, adminRes, viewerRes, subRes, weekRes, owedRes] = await Promise.all([
        orgApi.get(`/org/programs/${programId}`),
        // Org admins always receive these; they are shown as a fixed part of
        // the list so nobody wonders why they got one.
        orgApi.get("/org/admins").catch(() => ({ data: { admins: [] } })),
        orgApi.get("/org/viewers").catch(() => ({ data: { viewers: [] } })),
        orgApi.get("/org/substitutes").catch(() => ({ data: { substitutes: [] } })),
        orgApi.get(`/org/programs/${programId}/week`).catch(() => ({ data: null })),
        orgApi.get(`/org/programs/${programId}/notes-owed`).catch(() => ({ data: null })),
      ]);
      setDetail(res.data);
      setAdmins((adminRes.data?.admins || []).filter((a) => a.active !== false));
      // Only viewers scoped to THIS program — an org-wide list here would
      // imply people can see a program they cannot.
      setViewers((viewerRes.data?.viewers || []).filter((v) => (v.program_ids || []).includes(programId)));
      setSubstitutes((subRes.data?.substitutes || []).filter((x) => x.active !== false));
      setWeek(weekRes.data);
      setNotesOwed(owedRes.data);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load this program"));
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab !== "sessions" || sessions) return;
    orgApi
      .get(`/org/programs/${programId}/sessions`)
      .then((res) => setSessions(res.data))
      .catch(() => setSessions({ sessions: [], failed: true }));
  }, [tab, sessions, programId]);

  useEffect(() => {
    if (tab !== "students" || students) return;
    orgApi
      .get(`/org/programs/${programId}/students`)
      .then((res) => setStudents(res.data))
      .catch(() => setStudents({ students: [], count: 0, failed: true }));
  }, [tab, students, programId]);

  /* The Sessions tab's rows through the shared filter (F4). The kind comes
     from the URL — a tile chose it — and the tile's window is a period. */
  const sessionRows = useMemo(() => (sessions?.sessions || []).map(sessionRow), [sessions]);
  const sessionKind = filter === "solo" ? "student" : filter === "class" ? "class" : "all";
  const sessionStatus = statusFromFilter(filter);
  /* A tile's own window is a period here, so the tab opens on exactly the
     rows the tile counted: the taught window behind, the upcoming one ahead. */
  const tileWindow = sessionBar.period === WINDOW_PERIOD ? sessions?.window : sessionBar.period === UPCOMING_PERIOD ? sessions?.upcoming_window : null;
  const shownSessions = useMemo(() => {
    const windowed = tileWindow ? inWindow(sessionRows, tileWindow) : sessionRows;
    const scoped = byStatus(windowed, sessionStatus);
    return sortChronologically(
      filterLessons(
        scoped.rows,
        { ...sessionBar, period: tileWindow ? "all" : sessionBar.period, kind: sessionKind, status: scoped.status },
        (r) => r.class_name || "",
      ),
    );
  }, [sessionRows, tileWindow, sessionBar, sessionKind, sessionStatus]);
  /* The first 25 (or 50, or 100) of the range, oldest at the top, with the
     later ones a click below (MoreRows). Select all still reaches every
     filtered row. */
  const pagedSessions = usePagedRows(
    shownSessions,
    [sessionBar.period, sessionBar.instructor, sessionBar.classId, sessionBar.search, sessionKind, sessionStatus, tileWindow?.start, tileWindow?.end].join("|"),
  );
  /* Ticking sessions on the Sessions tab, every one of them, to clear
     notices, export, archive or delete (useOrgSessionActions), and clearing
     from the Overview's missing-notes card (useOrgNotesClear). Either one
     refreshes both lists, and the per-class counts that come with them. */
  const refreshNotes = useCallback(() => {
    setSessions(null);
    orgApi
      .get(`/org/programs/${programId}/notes-owed`)
      .then((res) => setNotesOwed(res.data))
      .catch(() => {});
  }, [programId]);
  const sessionsClearing = useOrgSessionActions({
    rows: shownSessions,
    onChanged: refreshNotes,
    testId: "program-sessions-clear",
    exportName: `hans-${exportSlug(detail?.program?.title, "program")}-sessions-selected`,
  });
  const owedRows = useMemo(
    () =>
      sortChronologically(
        (notesOwed?.sessions || []).map((n) => ({
          ...n,
          lesson_id: `owed:${n.class_id}:${n.occurrence_id || n.date}`,
          class_name: n.display_name,
          missing: true,
        })),
      ),
    [notesOwed],
  );
  const owedClearing = useOrgNotesClear({ rows: owedRows, onChanged: refreshNotes, testId: "program-owed-clear" });
  const sessionInstructors = useMemo(() => distinctOptions(sessionRows, (r) => r.user_id, (r) => r.instructor_name), [sessionRows]);
  const sessionClasses = useMemo(() => distinctOptions(sessionRows, (r) => r.class_id, (r) => r.class_name), [sessionRows]);

  if (loading) {
    return <PageShell width="wide" className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</PageShell>;
  }
  if (!detail?.program) return null;

  const { program, members = [], invited_members: invited = [], applications = [], taught, upcoming, classes = [] } = detail;
  const liveClasses = classes.filter((c) => !c.archived);
  const archivedClasses = classes.filter((c) => c.archived);
  const shownClasses = showArchived ? classes : liveClasses;
  const pending = applications.filter((a) => a.status === "pending");
  // The missing-notes list, per class. The section below it already answers
  // "which sessions"; this answers "which class", which is the one an
  // administrator acts on — you chase an instructor about a class, not about
  // the fourteenth of October.
  const owedByClass = (notesOwed?.sessions || []).reduce((acc, n) => {
    acc[n.class_id] = (acc[n.class_id] || 0) + 1;
    return acc;
  }, {});
  // The program's TYPE decides what it can hold: private lessons are booked,
  // so they have no classes and nobody to substitute for.
  const classBased = ["class", "camp"].includes(program.program_type || "private_lessons");
  // Booked in Scheduler, a private-lessons program's sessions are not Hans's
  // to count; a class program with some meetings there says so beside the number.
  const upcomingValue = !upcoming || !classBased ? "—" : upcoming.count;
  const upcomingSub = !upcoming
    ? undefined
    : !classBased
      ? "Booked in Scheduler"
      : upcoming.complete
        ? upcoming.window?.label
        : `${upcoming.window?.label} · Hans classes only`;

  const decide = async (applicationId, action) => {
    try {
      await orgApi.post(`/org/programs/${programId}/applications/${applicationId}/${action}`);
      toast.success(action === "accept" ? "Instructor added to the program" : "Application declined");
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't update the application"));
    }
  };

  /* Same blob round-trip the reporting page's exports use: it keeps the
     download on the api client (so a 401 still redirects to sign-in) and
     reports a failure as a toast instead of navigating the admin away from
     the page and onto an error body. */
  const exportProgram = async () => {
    setExporting(true);
    try {
      const resp = await orgApi.get(`/org/programs/${programId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `hans-${(program.title || "program").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "program"}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't download that export"));
    } finally {
      setExporting(false);
    }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from this program?`)) return;
    try {
      await orgApi.delete(`/org/programs/${programId}/instructors/${userId}`);
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't remove the instructor"));
    }
  };

  return (
    <PageShell width="wide">
      <button
        type="button"
        onClick={() => navigate("/org/programs")}
        className="mb-4 inline-flex items-center gap-1 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <Icon name="chevronLeft" size={15} /> All programs
      </button>

      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">{program.title}</h1>
            {/* Closed is what archiving a program writes. */}
            <Tag accent={program.status === "open"} data-testid="program-status">
              {program.status === "closed" ? "archived" : program.status}
            </Tag>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Tell the program's people something: preset to every instructor
                and viewer, narrowed to chosen instructors or classes. */}
            <Button size="sm" icon="bell" onPress={() => setDialog("announce")} data-testid="open-announcement">
              Send announcement
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon="download"
              onPress={exportProgram}
              disabled={exporting}
              data-testid="export-program"
            >
              {exporting ? "Preparing…" : "Export"}
            </Button>
          </div>
        </div>
        {program.description && (
          <p className="mt-1.5 max-w-3xl font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">{program.description}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-sans text-[13px] text-[var(--text-tertiary)]">
          {program.meeting_times && <span>{program.meeting_times}</span>}
          {program.location && <span>{program.location}</span>}
          {(program.instruments || []).length > 0 && <span>{program.instruments.join(", ")}</span>}
        </div>
      </div>

      {/* ---- Taught in this program -------------------------------------- */}
      {/* The program's vital signs, directly under its title and above the
          tab bar (decision D1): once they open the tabs that hold the rows
          behind each number, a control that navigates BETWEEN tabs cannot
          live inside one. Hidden on Settings, where they are noise beside a
          form. */}
      {tab !== "settings" && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="program-tiles">
          {/* Each tile opens the tab that holds the rows behind its number
              (F6): the Sessions tab on the tile's own window and kind, the
              Students tab on the population the reach figure counts. */}
          <StatCard compact value={taught?.sessions ?? "—"} label="Sessions" sub={taught?.window_label} data-testid="program-tile-sessions"
            onPress={() => { setSessionBar((b) => ({ ...b, period: WINDOW_PERIOD })); setTab("sessions", "taught"); }} />
          <StatCard compact value={taught?.class_sessions ?? "—"} label="Class sessions" data-testid="program-tile-class-sessions"
            onPress={() => { setSessionBar((b) => ({ ...b, period: WINDOW_PERIOD })); setTab("sessions", "class"); }} />
          {/* What is coming in the next 30 days at the venue. A program whose
              sessions are booked in Kalver Scheduler cannot be counted here. */}
          <StatCard compact value={upcomingValue} label="Upcoming" sub={upcomingSub} data-testid="program-tile-upcoming"
            onPress={() => { setSessionBar((b) => ({ ...b, period: UPCOMING_PERIOD })); setTab("sessions", "upcoming"); }} />
          <StatCard compact value={taught?.students_reached ?? "—"} label="Students reached" data-testid="program-tile-students"
            onPress={() => setTab("students")} />
        </div>
      )}

      {/* Four tabs are wider than a phone. The bar scrolls within itself on a
          narrow screen rather than pushing the page sideways, as the class
          page's does. */}
      <div className="-mx-4 mb-6 overflow-x-auto px-4 md:mx-0 md:px-0" data-testid="program-tabs-scroller">
        <SegmentedControl
          className="whitespace-nowrap"
          testId="program-detail-tabs"
          value={tab}
          onChange={(id) => setTab(id)}
          options={PROGRAM_TABS}
        />
      </div>

      {/* ---- Sessions ------------------------------------------------ */}
      {/* Every session in the program, one word each for what happened,
          never a summary. Opened from a tile, the rows are the tile's own. */}
      {tab === "sessions" && (
        <>
          <SectionHeader>
            Sessions ({shownSessions.length})
            {tileWindow && (
              <span className="ml-3 font-sans text-[13px] font-normal text-[var(--text-tertiary)]" data-testid="program-sessions-window">
                {tileWindow.label}
              </span>
            )}
          </SectionHeader>
          <p className="mb-3 max-w-2xl font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="program-sessions-boundary">
            Date, class, who taught it and whether it was written up. What was said in a lesson takes the lesson-notes
            viewer grant and is never listed here.
          </p>
          <LessonFilterBar
            value={{ ...sessionBar, kind: sessionKind, status: sessionStatus }}
            onChange={(next) => {
              const kindFilter = next.kind === "student" ? "solo" : next.kind === "class" ? "class" : null;
              const statusFilter = STATUS_FILTERS[next.status] || null;
              const wanted = next.kind !== sessionKind ? kindFilter : next.status !== sessionStatus ? statusFilter : filter || null;
              if (wanted !== (filter || null)) setTab("sessions", wanted || undefined);
              // Asking for upcoming sessions while looking back 30 days looks
              // ahead instead: there is nothing upcoming behind today.
              const period =
                next.status === "upcoming" && sessionStatus !== "upcoming" && next.period === WINDOW_PERIOD ? UPCOMING_PERIOD : next.period;
              setSessionBar({ period, instructor: next.instructor, classId: next.classId, search: next.search });
            }}
            periods={[
              { id: WINDOW_PERIOD, label: sessions?.window?.label || "Last 30 days" },
              { id: UPCOMING_PERIOD, label: sessions?.upcoming_window?.label || "Next 30 days" },
              ...PERIODS,
            ]}
            kinds={SESSION_KINDS}
            statuses={SESSION_STATUSES}
            instructors={sessionInstructors}
            classes={sessionClasses}
            unit="sessions"
            searchPlaceholder="Search sessions"
          />
          {sessionStatus === "archived" && <ArchivedNote testId="program-archived-note" />}
          <SessionList
            loaded={Boolean(sessions)}
            failed={sessions?.failed}
            total={sessionRows.length}
            rows={pagedSessions.shown}
            onOpenClass={(classId) => navigate(`/org/classes/${classId}`)}
            emptyText="Nothing scheduled or taught in this program yet."
            testId="program-sessions"
            rowTestId="program-session"
            header={sessionsClearing.toolbar}
            leadingFor={sessionsClearing.checkbox}
          />
          {sessions && !sessions.failed && (
            <MoreRowsBar paged={pagedSessions} unit="session" testId="program-sessions-more" className="-mt-3 mb-6" />
          )}
          {sessionsClearing.dialogs}
        </>
      )}

      {/* ---- Students ------------------------------------------------ */}
      {/* The population the reach tile counts: the same rows, by construction. */}
      {tab === "students" && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <SectionHeader className="mb-0">Students ({students?.count ?? "…"})</SectionHeader>
            {/* The roster as this tab lists it, and attendance for every
                class in the program, a tab per class. */}
            <ExportMenu
              client={orgApi}
              errorMessage={getOrgErrorMessage}
              testId="export-program-students"
              items={[
                {
                  id: "roster",
                  label: "Roster",
                  hint: "CSV · every student in this program",
                  url: `/org/programs/${programId}/students/export`,
                  filename: `hans-${exportSlug(program.title, "program")}-roster.csv`,
                  type: CSV_TYPE,
                },
                {
                  id: "attendance",
                  label: "Attendance",
                  hint: "Spreadsheet · a tab for each class",
                  url: `/org/programs/${programId}/attendance/export`,
                  filename: `hans-${exportSlug(program.title, "program")}-attendance.xlsx`,
                  type: XLSX_TYPE,
                },
              ]}
            />
          </div>
          <Card padding={0} className="mb-6" data-testid="program-students">
            {!students ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
            ) : students.failed ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load the students.</div>
            ) : students.students.length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">No students on this program&rsquo;s rosters yet.</div>
            ) : (
              students.students.map((st, i) => (
                <ListRow
                  key={st.student_id}
                  borderBottom={i < students.students.length - 1}
                  data-testid={`program-student-${st.student_id}`}
                  title={st.name}
                  subtitle={[
                    st.classes.map((c) => c.name).join(", ") || null,
                    st.solo ? "Solo lessons" : null,
                    st.instructor_name,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  trailing={
                    <span className="flex items-center gap-2">
                      {st.archived && <Tag>Archived</Tag>}
                      <Tag accent={st.consent_status === "granted"}>{CONSENT_LABELS[st.consent_status] || st.consent_status}</Tag>
                    </span>
                  }
                  onPress={st.classes[0] ? () => navigate(`/org/classes/${st.classes[0].class_id}`) : undefined}
                />
              ))
            )}
          </Card>
        </>
      )}

      {tab === "settings" && (
        <>
          <ProgramListingSettings
            program={program}
            onSaved={(saved) => saved && setDetail((d) => ({ ...d, program: saved }))}
          />
          {/* The address every class in the program meets at, and what a
              published timetable's calendar blocks carry. A program fact, so
              it is set here and nowhere else. */}
          <ProgramLocationSettings
            key={JSON.stringify(program.location_address || null)}
            program={program}
            onSaved={(saved) => saved && setDetail((d) => ({ ...d, program: saved }))}
          />
          {/* Switches you set once belong beside the other settings, not in an
              overview that answers "how is this program going". */}
          <ProgramEmailSettings
            program={program}
            admins={admins}
            onChanged={(next) => next && setDetail((d) => ({ ...d, program: next }))}
          />
          {/* Whether instructors may ask for cover, and on what terms
              (docs/substitute-requests.md). */}
          <ProgramSubstituteSettings
            program={program}
            onSaved={(saved) => saved?.program_id && setDetail((d) => ({ ...d, program: saved }))}
          />
          {/* Last, and apart: archiving and deleting act on the whole program.
              Either one changes its classes too, so the page is read again. */}
          <ProgramLifecycleSettings program={program} onChanged={load} />
        </>
      )}

      {tab === "overview" && (
        <>
      {/* ---- Classes and instructors, side by side -------------------- */}
      {/* First on the Overview, above the week. Two columns: the page was a
          single narrow stack with a lot of air, and these two lists are read
          together. Each keeps its own add button underneath, so the action
          sits with the thing it adds to. */}
      {/* Side by side only at desktop width: at a tablet's width inside the
          rail, two columns truncate every name in both lists. */}
      <div className={`grid items-start gap-x-6 ${classBased ? "xl:grid-cols-2" : ""}`}>
        <div className="min-w-0">
          {/* ---- Classes ------------------------------------------------- */}
          {/* A private-lessons program is scheduled by its bookings, so it has
              nothing for a class to be — no section rather than an empty one. */}
          {classBased && (
            <>
          <SectionHeader>Classes ({liveClasses.length})</SectionHeader>
          <Card padding={0} className="mb-6">
            {shownClasses.length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">No classes in this program yet.</div>
            ) : (
              shownClasses.map((c, i) => (
                <ListRow
                  key={c.class_id}
                  borderBottom={i < shownClasses.length - 1}
                  /* A subtle wash, not a red row: this is work outstanding,
                     not a failure, and every class in a term that has just
                     started would otherwise light up at once. The same
                     accent-dim the rest of the product uses for "needs
                     attention", so it reads the same way here as everywhere. */
                  className={owedByClass[c.class_id] ? "bg-[var(--accent-yellow-dim)]" : undefined}
                  data-notes-owed={owedByClass[c.class_id] || undefined}
                  title={c.name}
                  /* An unassigned class says so instead of printing an empty
                     name. It is a real state, not a missing value: the org
                     made the class before it staffed the term — and the
                     subtitle carries it, so the row keeps its width for the
                     class's own name. */
                  subtitle={
                    c.instructor_id
                      ? `${c.instructor_name} · ${c.member_count} students`
                      : "No instructor yet"
                  }
                  meta={
                    !c.instructor_id
                      ? null
                      : c.next_session_at
                        ? `Next ${when(c.next_session_at)}`
                        : "No upcoming session"
                  }
                  onPress={() => navigate(`/org/classes/${c.class_id}`)}
                  trailing={
                    <span className="flex shrink-0 items-center gap-2">
                    {c.archived ? <Tag data-testid={`class-archived-${c.class_id}`}>Archived</Tag> : null}
                    {owedByClass[c.class_id] ? (
                      <Tag accent data-testid={`class-notes-owed-${c.class_id}`}>
                        {owedByClass[c.class_id]} missing
                      </Tag>
                    ) : null}
                    {!c.instructor_id ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="shrink-0"
                        data-testid={`assign-class-${c.class_id}`}
                        onPress={(e) => {
                          // The row itself opens the class. Without this the
                          // click does both: the dialog opens behind a
                          // navigation that has already left the page.
                          e.stopPropagation();
                          setAssigning(c);
                          setDialog("assign");
                        }}
                      >
                        Assign
                      </Button>
                    ) : /* `connected` (scheduler_offering_id), not `kind`: kind is the
                          shape of the group (studio_class / ensemble / cohort) and
                          says nothing about where the schedule lives. */
                    !c.connected ? (
                      <Tag>Not linked</Tag>
                    ) : (
                      <span className="font-mono text-[15px] text-[var(--text-primary)]">{c.sessions_taught}</span>
                    )}
                    </span>
                  }
                />
              ))
            )}
            {archivedClasses.length > 0 && (
              <div className="border-t border-[var(--studio-divider)] px-[18px] py-3">
                <button
                  type="button"
                  data-testid="toggle-archived-classes"
                  onClick={() => setShowArchived((v) => !v)}
                  className="font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                >
                  {showArchived ? "Hide archived" : `Show ${archivedClasses.length} archived`}
                </button>
              </div>
            )}
          </Card>

          <div className="mb-6">
            <Button variant="ghost" onPress={() => setDialog("class")} icon="plus" data-testid="open-add-class">
              Add class
            </Button>
          </div>
            </>
          )}

        </div>

        <div className="min-w-0">
          {/* ---- Instructors --------------------------------------------- */}
          <SectionHeader>Instructors ({members.length})</SectionHeader>
          <Card padding={0} className="mb-3">
            {members.length === 0 && pending.length === 0 && invited.length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                No instructors yet — accept an application or invite one below.
              </div>
            ) : (
              members.map((m, i) => (
                <ListRow
                  key={m.user_id}
                  borderBottom={i < members.length - 1 || invited.length > 0 || pending.length > 0}
                  leading={<Avatar name={m.name} size={36} />}
                  title={m.name}
                  subtitle={m.student_count != null ? `${m.student_count} students` : undefined}
                  onPress={() => navigate(`/org/instructors/${m.user_id}`)}
                  trailing={
                    <IconButton
                      icon="trash"
                      size={32}
                      danger
                      ariaLabel={`Remove ${m.name}`}
                      onPress={() => removeMember(m.user_id, m.name)}
                    />
                  }
                />
              ))
            )}
            {/* Invitations already out, shown as provisional. They are NOT
                members — acceptance is what creates the membership every
                roster follows — but an org that just invited three people
                should see three people rather than an empty list. */}
            {invited.map((v, i) => (
              <ListRow
                key={v.invite_id}
                data-testid={`invited-${v.invite_id}`}
                borderBottom={i < invited.length - 1 || pending.length > 0}
                leading={<Avatar name={v.name || v.email} size={36} />}
                title={v.name || v.email}
                subtitle={v.name && v.email ? v.email : "Invitation sent"}
                trailing={<Tag>Invited</Tag>}
              />
            ))}
            {pending.map((a, i) => (
              <div
                key={a.application_id}
                data-testid={`application-${a.application_id}`}
                className={`px-[18px] py-3 ${i < pending.length - 1 ? "border-b border-[var(--studio-divider)]" : ""}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-sans text-[15px] text-[var(--text-primary)]">{a.instructor_name}</div>
                    <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">Applied</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button size="sm" onPress={() => decide(a.application_id, "accept")} data-testid={`application-accept-${a.application_id}`}>
                      Accept
                    </Button>
                    <button
                      type="button"
                      onClick={() => decide(a.application_id, "decline")}
                      data-testid={`application-decline-${a.application_id}`}
                      className="font-sans text-[13px] font-medium text-[var(--state-danger)] hover:underline"
                    >
                      Decline
                    </button>
                  </div>
                </div>
                {a.message && <p className="mt-1 font-sans text-[14px] text-[var(--text-secondary)]">{a.message}</p>}
              </div>
            ))}
          </Card>
          <div className="mb-6">
            <Button variant="ghost" onPress={() => setDialog("instructors")} icon="plus" data-testid="open-invite-instructors">
              Invite instructors
            </Button>
          </div>
        </div>
      </div>

      {/* ---- This week and who taught, side by side ------------------- */}
      {/* What is coming beside what has been done. Half the width each at
          desktop width; on a narrower screen Taught drops back under the
          week, the same rule as the other two-column rows. */}
      <div className="grid items-start gap-x-6 xl:grid-cols-2" data-testid="program-week-and-taught">
        <div className="min-w-0" data-testid="program-week-column">
          {/* ---- This week ----------------------------------------------- */}
          <SectionHeader>This week</SectionHeader>
          <Card padding={0} className="mb-6">
            {week?.source === "scheduler" ? (
              /* A private-lessons program is scheduled by its bookings, which live
                 in Scheduler. An empty calendar here would read as "nothing is
                 happening", which is a different claim from "Hans cannot see it". */
              <div className="px-[18px] py-6" data-testid="week-scheduler">
                <p className="font-sans text-[15px] text-[var(--text-primary)]">
                  Families book this program directly, so its week lives in Kalver Scheduler.
                </p>
                <p className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]">
                  Hans shows what was taught once its instructor writes notes. The booking link isn&rsquo;t available here
                  yet.
                </p>
              </div>
            ) : (week?.meetings || []).length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                {classBased && classes.length === 0
                  ? "No classes yet — add one and give it a schedule."
                  : "Nothing scheduled in the next seven days."}
              </div>
            ) : (
              week.meetings.map((m, i) => (
                <ListRow
                  key={`${m.class_id}-${m.date}-${m.time_local}`}
                  borderBottom={i < week.meetings.length - 1}
                  title={m.class_name}
                  subtitle={`${m.instructor_name} · ${m.time_local}`}
                  meta={m.date}
                  trailing={m.status === "cancelled" ? <Tag>No class</Tag> : undefined}
                  onPress={() => navigate(`/org/classes/${m.class_id}`)}
                />
              ))
            )}
          </Card>
        </div>
        <div className="min-w-0" data-testid="program-taught-column">
          {/* ---- Taught in this program ---------------------------------- */}
          {/* The breakdown behind the tiles above: who taught how much. */}
          <SectionHeader>Taught in this program</SectionHeader>
          <Card padding={0} className="mb-6" data-testid="program-taught">
            {(taught?.by_instructor || []).length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                Nothing taught in the {(taught?.window_label || "Last 30 days").toLowerCase()}.
              </div>
            ) : (
              taught.by_instructor.map((row, i) => (
                <ListRow
                  key={row.user_id}
                  borderBottom={i < (taught.by_instructor.length - 1)}
                  leading={<Avatar name={row.name} size={36} />}
                  title={row.name}
                  subtitle={`${row.class_sessions} class · ${row.solo} solo`}
                  meta={row.last_taught_at ? `Last taught ${when(row.last_taught_at)}` : undefined}
                  trailing={<span className="font-mono text-[15px] text-[var(--text-primary)]">{row.sessions}</span>}
                  onPress={() => navigate(`/org/instructors/${row.user_id}`)}
                />
              ))
            )}
          </Card>
        </div>
      </div>

      {/* ---- Viewers and substitutes, side by side -------------------- */}
      {/* The people around the program who do not teach it: who watches it,
          and who stands in. Half the width each at desktop width, the same
          rule as the classes and instructors above; a private-lessons program
          has no substitutes, so its viewers keep the whole width. */}
      <div className={`grid items-start gap-x-6 ${classBased ? "xl:grid-cols-2" : ""}`} data-testid="program-people">
        <div className="min-w-0" data-testid="program-viewers">
          {/* ---- Viewers ----------------------------------------------- */}
          <SectionHeader
            action="+ Add viewer"
            onAction={() => setDialog("viewer")}
            actionTestId="open-add-viewer"
          >
            Viewers ({viewers.length})
          </SectionHeader>
          <Card padding={0} className="mb-6">
            {viewers.length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                Nobody is watching this program yet. A viewer sees what you allow and can change nothing.
              </div>
            ) : (
              viewers.map((v, i) => (
                <ListRow
                  key={v.admin_id}
                  borderBottom={i < viewers.length - 1}
                  leading={<Avatar name={[v.first_name, v.last_name].filter(Boolean).join(" ") || v.email} size={36} />}
                  title={[v.first_name, v.last_name].filter(Boolean).join(" ") || v.email}
                  subtitle={permissionLabels(v.permissions).join(", ") || "Nothing granted yet"}
                  wrapText
                  trailing={v.pending ? <Tag>Invited</Tag> : undefined}
                  data-testid={`viewer-row-${v.admin_id}`}
                  onPress={() => setEditingViewer(v)}
                />
              ))
            )}
          </Card>
        </div>

        {/* ---- Substitutes --------------------------------------------- */}
        {/* A private-lessons program has no class sessions for anyone to stand
            in on, so the whole section is absent rather than empty. */}
        {classBased && (
          <div className="min-w-0" data-testid="program-substitutes">
            <SectionHeader
              action="+ Add substitute"
              onAction={() => setDialog("substitute")}
              actionTestId="open-add-substitute"
            >
              Substitutes ({substitutes.length})
            </SectionHeader>
            <Card padding={0} className="mb-6">
              {substitutes.length === 0 ? (
                <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                  No substitutes yet. Add one to assign a session they covered.
                </div>
              ) : (
                substitutes.map((x, i) => (
                  <ListRow
                    key={x.substitute_id}
                    borderBottom={i < substitutes.length - 1}
                    leading={<Avatar name={x.name} size={36} />}
                    title={x.name}
                    subtitle={x.note || x.email || undefined}
                    trailing={<Tag>Substitute</Tag>}
                  />
                ))
              )}
            </Card>
          </div>
        )}
      </div>

      {/* ---- Substitute requests ------------------------------------- */}
      {/* What instructors have asked for, in its own full-width section under
          the viewers and the roster: a request carries a reason, a plan and
          two buttons, which a half-width column squeezed. */}
      {classBased && <SubstituteRequestsCard programId={programId} substitutes={substitutes} />}

      {/* ---- Notes owed ---------------------------------------------- */}
      {/* `complete: false` means some sessions could not be checked, so an
          empty list is NOT an all-clear — the two render differently. The
          heading is the dropdown: closed to start, it says how many are owed,
          and the classes above carry their own counts. A private-lessons
          program has no class sessions to owe notes for (its read says
          `complete: false` with nothing in it), so it has no section, as on
          iOS (showsMissingNotes). */}
      {classBased && notesOwed && ((notesOwed.count ?? 0) > 0 || notesOwed.complete === false) && (
        <>
          <SectionHeader className={owedOpen ? undefined : "mb-6"}>
            <button
              type="button"
              onClick={() => setOwedOpen((v) => !v)}
              aria-expanded={owedOpen}
              data-testid="toggle-notes-owed"
              className="-mx-1.5 inline-flex items-center gap-1.5 rounded-lg px-1.5 py-0.5 transition duration-150 hover:bg-[var(--studio-overlay)] active:scale-95"
            >
              Missing notes ({notesOwed.count})
              <Icon name={owedOpen ? "chevronUp" : "chevronDown"} size={17} className="text-[var(--text-tertiary)]" />
            </button>
          </SectionHeader>
          {owedOpen && (
            <Card padding={0} className="mb-6" data-testid="program-notes-owed">
              {owedClearing.toolbar}
              {owedRows.map((n, i) => {
                const asked = clearanceTag(n.notes_clearance);
                return (
                  <ListRow
                    key={n.lesson_id}
                    borderBottom={i < owedRows.length - 1}
                    leading={owedClearing.checkbox(n)}
                    title={n.display_name}
                    subtitle={[n.instructor_name, n.time_local, clearanceLine(n.notes_clearance)].filter(Boolean).join(" · ")}
                    meta={n.date}
                    trailing={<Tag accent={Boolean(asked)}>{asked ? asked.label : "No notes"}</Tag>}
                    onPress={() => navigate(`/org/classes/${n.class_id}`)}
                    data-testid={`program-owed-${n.lesson_id}`}
                  />
                );
              })}
              {!notesOwed.complete && (
                <div data-testid="notes-incomplete" className="px-[18px] py-4 font-sans text-[13px] text-[var(--text-tertiary)]">
                  Some of this program&rsquo;s classes are scheduled in Kalver Scheduler, and Hans can&rsquo;t check
                  those sessions — this list may be incomplete.
                </div>
              )}
            </Card>
          )}
          {owedClearing.dialogs}
        </>
      )}

      {/* ---- Contacts and other resources ----------------------------- */}
      {/* What instructors need to hand before they turn up: who to call at
          the school and at the organization, and the school's own papers (a
          safety plan, a floor plan). Both reach the instructors' program page. */}
      <ProgramContactsCard
        key={program.updated_at || program.program_id}
        program={program}
        orgName={org?.name}
        admins={admins}
        onSaved={(saved) => saved && setDetail((d) => ({ ...d, program: saved }))}
      />
      <ResourcesSection
        basePath={`/org/programs/${programId}`}
        scope="program"
        examples="a safety plan, the school's floor plan, a parking map"
      />
        </>
      )}

      <InviteInstructorsDialog
        open={dialog === "instructors"}
        onOpenChange={(v) => setDialog(v ? "instructors" : null)}
        programId={programId}
        unassignedClasses={liveClasses.filter((c) => !c.instructor_id)}
        onInvited={load}
      />
      <AddViewerDialog
        open={dialog === "viewer"}
        onOpenChange={(v) => setDialog(v ? "viewer" : null)}
        programs={[{ program_id: programId, title: program.title }]}
        defaultProgramId={programId}
        onAdded={load}
      />
      <AddViewerDialog
        open={Boolean(editingViewer)}
        onOpenChange={(v) => !v && setEditingViewer(null)}
        programs={[{ program_id: programId, title: program.title }]}
        viewer={editingViewer}
        onAdded={load}
      />
            <AddSubstituteDialog
        open={dialog === "substitute"}
        onOpenChange={(v) => setDialog(v ? "substitute" : null)}
        onAdded={load}
      />
      <AddClassDialog
        open={dialog === "class"}
        onOpenChange={(v) => setDialog(v ? "class" : null)}
        programId={programId}
        instructors={members}
        invited={invited}
        onCreated={(made) => {
          load();
          // Straight to the class, because a class without a schedule is not
          // finished — the next thing to do is give it one. Unless nobody is
          // on it yet, in which case there is nothing to do there: the
          // schedule cannot be built until it is assigned.
          if (made?.class_id && made?.user_id) navigate(`/org/classes/${made.class_id}`);
        }}
      />
      <AnnouncementDialog
        open={dialog === "announce"}
        onOpenChange={(v) => setDialog(v ? "announce" : null)}
        programId={programId}
        programTitle={program.title}
        instructors={members}
        classes={liveClasses}
        viewerCount={viewers.length}
      />
            <AssignClassInstructorDialog
        open={dialog === "assign"}
        onOpenChange={(v) => setDialog(v ? "assign" : null)}
        klass={assigning}
        instructors={members}
        onAssigned={load}
      />
    </PageShell>
  );
}
